import Store from 'store/store';
import { getShowtimesData } from 'utils/bolt/showtimes';
import { getScreeningDetailsFormatted } from 'helpers/screeningDetails';
import QRCode from 'qrcode';
import DeviceDetector from "device-detector-js";

const LINK_COLOR = '#0095D0';
const TEXT_COLOR = '#00061C';
const CONCESSION_TEXT_COLOR = '#08A7E1';
const MAX_WIDTH = 400;
const MAX_NAME_WIDTH = 170;
const MAX_EMAIL_WIDTH = 300;
const MAX_NAME_WIDTH_FOR_TRUNCATION = 200;
const MAX_EMAIL_WIDTH_FOR_TRUNCATION = 330;


const THEATER_HEADER_TOP_Y = 150;
const THEATER_LOGO_WIDTH = 40;
const THEATER_HEADER_MID_Y = THEATER_HEADER_TOP_Y + THEATER_LOGO_WIDTH / 2;
const PIN_TOP_Y = THEATER_HEADER_TOP_Y + THEATER_LOGO_WIDTH + 12;
const PIN_WIDTH = 16;
const PIN_MID_Y = PIN_TOP_Y + PIN_WIDTH / 2;
const MOVIE_NAME_MID_Y = PIN_TOP_Y + PIN_WIDTH + 32 + 14;
const SCREENING_DETAILS_TOP_Y = MOVIE_NAME_MID_Y + 14 + 24;
const SCREENING_ICONS_WIDTH = 20;
const SCREENING_DETAILS_MID_Y = 1 + SCREENING_DETAILS_TOP_Y + SCREENING_ICONS_WIDTH / 2;
const NAME_TOP_Y = SCREENING_DETAILS_TOP_Y + SCREENING_ICONS_WIDTH + 32 + 1 + 24;
const CUSTOMER_NAME_TOP_Y = NAME_TOP_Y + 18 + 4;
const EMAIL_TOP_Y = CUSTOMER_NAME_TOP_Y + 20 + 24 + 1 + 24;
// const CUSTOMER_EMAIL_TOP_Y = EMAIL_TOP_Y + 18 + 4;
const LEGAL_LINE_1_TOP_Y = CUSTOMER_NAME_TOP_Y + 20 + 24 + 1 + 20;
const LEGAL_LINE_2_TOP_Y = LEGAL_LINE_1_TOP_Y + 45;
const LEGAL_LINE_3_TOP_Y = LEGAL_LINE_2_TOP_Y + 25;
const LEGAL_LINE_4_TOP_Y = LEGAL_LINE_3_TOP_Y + 20;
const LEGAL_LINE_5_TOP_Y = LEGAL_LINE_4_TOP_Y + 30;

const EARLY_ACCESS_LINK_COLOR = '#FF9900';
const EARLY_ACCESS_LEGAL_LINE_1_TOP_Y = CUSTOMER_NAME_TOP_Y + 20 + 24 + 1 + 20;
const EARLY_ACCESS_LEGAL_LINE_2_TOP_Y = EARLY_ACCESS_LEGAL_LINE_1_TOP_Y + 20;
const EARLY_ACCESS_LEGAL_LINE_3_TOP_Y = EARLY_ACCESS_LEGAL_LINE_2_TOP_Y + 60;
const EARLY_ACCESS_LEGAL_LINE_4_TOP_Y = EARLY_ACCESS_LEGAL_LINE_3_TOP_Y + 35;

const CONCESSION_LINE_1_TOP_Y = LEGAL_LINE_1_TOP_Y + CUSTOMER_NAME_TOP_Y / 2;
const CONCESSION_LINE_2_TOP_Y = CONCESSION_LINE_1_TOP_Y + 30;
const CONCESSION_ICONS_WIDTH = 20;
const CONCESSION_ICONS_MID = CONCESSION_LINE_2_TOP_Y + CONCESSION_ICONS_WIDTH / 2;
const CONCESSION_LINE_3_TOP_Y = CONCESSION_LINE_2_TOP_Y + 33;
const CONCESSION_QRCODE_WIDTH = 133;

const scaleFactor = 2.8;

const loadImg = async (src) => {
	const image = await new Promise((resolve, reject) => {
		const image = document.createElement('img');
		image.crossOrigin = 'anonymous';
		image.src = src + '?_cors=1';
		image.onload = () => resolve(image);
		image.onerror = reject;
	});
	return image;
};

const formatTheaterDetail = (detail) => {
	return detail ? detail + ', ' : '';
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
	let words = text.split(' ');
	let line = ''; 
	let testLine = ''; 
	let lineArray = []; 

	for (let i = 0; i < words.length; i++) {
		testLine += `${words[i]} `;
		let metrics = ctx.measureText(testLine);
		let testWidth = metrics.width;
		if (testWidth > maxWidth && i > 0) {
			lineArray.push([line, x, y]);
			y += lineHeight;
			line = `${words[i]} `;
			testLine = `${words[i]} `;
		}
		else {
			line += `${words[i]} `;
		}
		if (i === words.length - 1) {
			lineArray.push([line, x, y]);
		}
	}

	lineArray.forEach(line => {
		ctx.fillText(line[0], line[1], line[2]);
	});
};


const generateQRCode = async (data) => {
	try {
		return await QRCode.toCanvas(data);
	} catch (error) {
		console.error('Error generating QR code:', error);
	}
};

const stringEllipsis = (ctx, text, maxWidth) => {
	let width = ctx.measureText(text).width;
	let truncatedText = '';
	let ellipsis = '...';
	let ellipsisWidth = ctx.measureText(ellipsis).width;
	if (width <= maxWidth || width <= ellipsisWidth) {
		return text;
	} else {
		for (let i = 0; i < text.length; i++) {
			truncatedText = text.substring(0, i) + ellipsis;
			const truncatedWidth = ctx.measureText(truncatedText).width;
			if (truncatedWidth > maxWidth) {
				break;
			}
		}
	}
	return truncatedText;
};

export const generateAndDownloadPdf = async () => {
	if (!window.jspdf) return;
	Store.emit('SHOW_FULL_LOADER', true);
	const { jsPDF } = window.jspdf;

	const store = Store.get() || {};

	const ticketData = store?.showtimes?.customer?.tickets || [];
	const ticket = ticketData[0];
	const theaterData = ticket?.theater || {};
	const appData = store?.appData || {};
	const sharedImageRoot = store.appData?.urls?.roots?.sharedImages || '';
	const imageRoot = appData?.urls?.roots?.image || '';
	const defaultFont = store?.appData?.styling?.variables?.defaultFont || 'proxima-nova';
	const logoShade = 'light';
	const logo = ticket?.theater?.chain?.logo || 'default.png';
	const logoLink = `${sharedImageRoot}/logos/${logoShade}/${logo}`;

	const isEarlyAccessTicket = !!(ticket.earlyAccessCode || ticketData[1]?.earlyAccessCode);
	const baseImagePath = isEarlyAccessTicket ? `${imageRoot}/ticket/ticket-download-base-early-access-gold-new.svg` : `${imageRoot}/ticket/ticket-download-base-new.svg`;
	
	const baseImage = await loadImg(baseImagePath);
	const theaterLogo = await loadImg(logoLink);
	const pinIcon = await loadImg(`${imageRoot}/ticket/pin.svg`);
	const calendarIcon = await loadImg(`${imageRoot}/ticket/calendar.svg`);
	const clockIcon = await loadImg(`${imageRoot}/ticket/clock.svg`);
	const reelIcon = await loadImg(`${imageRoot}/ticket/reel.svg`);
	const ticketIcon = await loadImg(`${imageRoot}/ticket/ticket.svg`);
	const concessionIcons = await loadImg(`${imageRoot}/ticket/concessionIcons.svg`);
	let movieName = store?.appData?.meta?.title?.locale || '';
	let theaterName = theaterData.name || '';

	const pdf = new jsPDF('p', 'mm', 'a4');
	pdf.setFont('Helvetica', 'normal', 400);
	pdf.setFontSize(12);
	pdf.setTextColor(`${isEarlyAccessTicket ? EARLY_ACCESS_LINK_COLOR : LINK_COLOR}`);
	const c = document.createElement('canvas');
	const docWidth = pdf.internal.pageSize.getWidth();
	const docHeight = pdf.internal.pageSize.getHeight();
	const context = c.getContext('2d');
	c.width = baseImage.naturalWidth * scaleFactor;
	c.height = baseImage.naturalHeight * scaleFactor;
	context.scale(scaleFactor, scaleFactor);
	// context.drawImage(baseImage, 0, 0);
	const deviceDetector = new DeviceDetector();
	const userAgent = window.navigator.userAgent;
	const device = deviceDetector.parse(userAgent);
	const isSafari = device.client.name.toLowerCase().includes('safari');

	
	const leftX = 90;

	const showtimesAppData = getShowtimesData();
	const concessionVouchers = showtimesAppData.concessionVouchers || {};
	// const concessionQRCode = await loadImg(`${imageRoot}/ticket/qr-code-sample.svg`);

	await ticketData.reduce(async (accum, ticket, index) => {
		await accum;
		if (index) {
			pdf.addPage();
		}
		context.clearRect(0, 0, c.width, c.height);
		context.drawImage(baseImage, 0, 0);

		//Theater name
		context.drawImage(theaterLogo, leftX, THEATER_HEADER_TOP_Y, THEATER_LOGO_WIDTH, THEATER_LOGO_WIDTH);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		context.textBaseline = 'middle';
		context.fillStyle = TEXT_COLOR;
		context.textAlign = 'left';
		context.fillText(theaterName, leftX + 52, THEATER_HEADER_MID_Y);

		//Theater Address
		context.drawImage(pinIcon, leftX, PIN_TOP_Y, PIN_WIDTH, PIN_WIDTH);
		context.font = `400 12px ${defaultFont}, sans-serif`;
		const street = formatTheaterDetail(theaterData.street);
		const city = formatTheaterDetail(theaterData.city);
		const state = theaterData.state ? theaterData.state + ' ' : '';
		const postcode = theaterData.postcode;
		const fullAddress = street + city + state + postcode;
		context.fillText(fullAddress, leftX + 24, PIN_MID_Y);

		//Movie Title
		context.font = `700 28px ${defaultFont}, sans-serif`;
		context.fillText(movieName, leftX, MOVIE_NAME_MID_Y);

		const formatted = getScreeningDetailsFormatted({
			date: ticket?.screening?.date || '',
			time: ticket?.screening?.time || '',
			format: ticket?.screening?.format
		}, getShowtimesData()?.clockFormat);

		//Screening Info
		context.drawImage(calendarIcon, leftX, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		const dateX = leftX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.date, dateX, SCREENING_DETAILS_MID_Y);
		const clockX = dateX + context.measureText(formatted.date).width + 16;
		context.drawImage(clockIcon, clockX, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const timeX = clockX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.time, timeX, SCREENING_DETAILS_MID_Y);
		const reelX = timeX + context.measureText(formatted.time).width + 16;
		context.drawImage(reelIcon, reelX, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const formatX = reelX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(formatted.format, formatX, SCREENING_DETAILS_MID_Y);
		const ticketX = formatX + context.measureText(formatted.format).width + 16;
		context.drawImage(ticketIcon, ticketX, SCREENING_DETAILS_TOP_Y, SCREENING_ICONS_WIDTH, SCREENING_ICONS_WIDTH);
		const numTicketsX = ticketX + SCREENING_ICONS_WIDTH + 8;
		context.fillText(`${index + 1} of ${ticketData.length}`, numTicketsX, SCREENING_DETAILS_MID_Y);

		//Name
		context.textBaseline = 'top';
		const copy = store?.appData?.copy?.baseCopy || {};
		context.font = `400 14px ${defaultFont}, sans-serif`;
		context.fillText(copy.$NAME, leftX, NAME_TOP_Y);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		context.fillText(stringEllipsis(context, ticket.userFullName, MAX_NAME_WIDTH_FOR_TRUNCATION), leftX, CUSTOMER_NAME_TOP_Y, MAX_NAME_WIDTH);
		//Email
		context.font = `400 14px ${defaultFont}, sans-serif`;
		context.fillText(copy.$EMAIL, leftX + 173, NAME_TOP_Y);
		context.font = `700 16px ${defaultFont}, sans-serif`;
		context.fillText(stringEllipsis(context, ticket.userEmail, MAX_EMAIL_WIDTH_FOR_TRUNCATION), leftX + 173, CUSTOMER_NAME_TOP_Y, MAX_EMAIL_WIDTH);

		//Legal Copy
		if (isEarlyAccessTicket) {
			context.font = `400 11px ${defaultFont}, sans-serif`;
			context.fillText(copy.$TICKET_PDF_LEGAL_1_EARLY_ACCESS, leftX, EARLY_ACCESS_LEGAL_LINE_1_TOP_Y);
			wrapText(context, copy.$TICKET_PDF_LEGAL_2_EARLY_ACCESS, leftX, EARLY_ACCESS_LEGAL_LINE_2_TOP_Y, MAX_WIDTH, 13);
			wrapText(context, copy.$TICKET_PDF_LEGAL_4_EARLY_ACCESS, leftX, EARLY_ACCESS_LEGAL_LINE_4_TOP_Y, MAX_WIDTH, 13);
			context.font = `700 11px ${defaultFont}, sans-serif`;
			wrapText(context, copy.$TICKET_PDF_LEGAL_3_EARLY_ACCESS, leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y, MAX_WIDTH, 13);
			// context.fillText('___________________________________________________________________________________________________', leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y, MAX_WIDTH, 13);
			// fake underline
			if (isSafari) {
				context.fillRect(leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y + 15, MAX_WIDTH - 5, 1);
				context.fillRect(leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y + 28, 100, 1);
			} else {
				context.fillRect(leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y + 10, MAX_WIDTH - 5, 1);
				context.fillRect(leftX, EARLY_ACCESS_LEGAL_LINE_3_TOP_Y + 23, 100, 1);
			}

		} else {
			context.font = `400 10px ${defaultFont}, sans-serif`;
			wrapText(context, copy.$TICKET_PDF_LEGAL_1, leftX, LEGAL_LINE_1_TOP_Y, MAX_WIDTH, 12);
			wrapText(context, copy.$TICKET_PDF_LEGAL_2, leftX, LEGAL_LINE_2_TOP_Y, MAX_WIDTH, 12);
			wrapText(context, copy.$TICKET_PDF_LEGAL_3, leftX, LEGAL_LINE_3_TOP_Y, MAX_WIDTH, 12);
			wrapText(context, copy.$TICKET_PDF_LEGAL_4, leftX, LEGAL_LINE_4_TOP_Y, MAX_WIDTH, 12);
			wrapText(context, copy.$TICKET_PDF_LEGAL_5, leftX, LEGAL_LINE_5_TOP_Y, MAX_WIDTH, 12);
		}
		//Concession QR Code section
		const chain = ticket?.theater?.chain;
		if (ticket.concessionCodeInfo?.code) {
			const concessionTitle = concessionVouchers[chain.id]?.title;
			const concessionSubtitle = concessionVouchers[chain.id]?.subtitle;
			const concessionDescription = concessionVouchers[chain.id]?.description;
			context.font = `700 18px ${defaultFont}, sans-serif`;
			context.setLineDash([6]);
			context.strokeRect(leftX - 53, CONCESSION_LINE_1_TOP_Y - 11, 520, 0);
			context.fillStyle = CONCESSION_TEXT_COLOR;
			context.fillText(concessionTitle, leftX, CONCESSION_LINE_1_TOP_Y);
			context.drawImage(concessionIcons, leftX, CONCESSION_LINE_2_TOP_Y, 30, CONCESSION_ICONS_WIDTH);
			context.font = `400 10px ${defaultFont}, sans-serif`;
			context.fillStyle = TEXT_COLOR;
			context.fillText(concessionSubtitle, leftX + 30, CONCESSION_ICONS_MID);
			wrapText(context, concessionDescription, leftX, CONCESSION_LINE_3_TOP_Y, MAX_WIDTH, 13);
			if (ticket.concessionCodeInfo?.isUnique) {
				const rightX = 390;
				const qr = await generateQRCode(ticket.concessionCodeInfo.code);
				context.drawImage(qr, rightX, CONCESSION_LINE_1_TOP_Y, CONCESSION_QRCODE_WIDTH, CONCESSION_QRCODE_WIDTH);
			}			
		}

		pdf.addImage(c.toDataURL('image/jpeg'), 'JPEG', 0, 0, docWidth, docHeight);
		pdf.textWithLink('Privacy Notice', 32, 225, { url: 'https://studios.amazon.com/help/privacy-notice' });
		pdf.textWithLink('Conditions of Use', 110, 225, { url: 'https://studios.amazon.com/help/conditions-of-use' });	

		// test - show canvas on screen
		// const ticketAreaDiv = document.querySelector("div[class^='ticketArea']");
		// ticketAreaDiv.appendChild(c);	
	}, '');

	pdf.save(`Prime-Premiere-${isEarlyAccessTicket ? 'Early-Access-' : ''}Passes-${movieName.replace(/\W+/g, '-')}.pdf`);
	Store.emit('SHOW_FULL_LOADER', false);
	Store.emit('TRACK_DOWNLOAD_TICKET', ({ theaterName, numTickets: ticketData.length }));
};